import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import BrandingService from "../services/BrandingService";
import {
  COLOR_VAR_MAP,
  type BrandingColors,
  type PublicBranding,
} from "../models/Branding";

interface BrandingContextType {
  logoUrl?: string;
  colors: BrandingColors;
  /** Apply a full branding payload (colors + logo). Used after login / live preview. */
  apply: (branding: PublicBranding) => void;
  /** Apply only colors (live preview while editing). */
  applyColors: (colors: BrandingColors) => void;
  resolveAssetUrl: (path: string | null | undefined) => string | undefined;
}

const BrandingContext = createContext<BrandingContextType | null>(null);
const service = new BrandingService();

function applyDocumentTitle(appName?: string | null) {
  if (appName && appName.trim()) document.title = appName.trim();
}

function applyFavicon(href?: string) {
  if (!href) return;
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  // Drop the static type so browsers infer it from the new asset (svg/png/ico).
  link.removeAttribute("type");
  link.href = href;
}

function applyCssVars(colors: BrandingColors, previous: string[]): string[] {
  const root = document.documentElement;
  // Clear any previously-applied overrides so removed values fall back to defaults.
  previous.forEach((v) => root.style.removeProperty(v));

  const applied: string[] = [];
  (Object.keys(COLOR_VAR_MAP) as (keyof BrandingColors)[]).forEach((key) => {
    const value = colors[key];
    if (value) {
      root.style.setProperty(COLOR_VAR_MAP[key], value);
      applied.push(COLOR_VAR_MAP[key]);
    }
  });
  return applied;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [logoUrl, setLogoUrl] = useState<string>();
  const [colors, setColors] = useState<BrandingColors>({});
  const appliedVars = useRef<string[]>([]);

  const applyColors = useCallback((next: BrandingColors) => {
    appliedVars.current = applyCssVars(next, appliedVars.current);
    setColors(next);
  }, []);

  const apply = useCallback(
    (branding: PublicBranding) => {
      applyColors(branding.colors ?? {});
      setLogoUrl(service.resolveAssetUrl(branding.logoUrl) ?? undefined);
      applyDocumentTitle(branding.appName);
      applyFavicon(service.resolveAssetUrl(branding.faviconUrl));
    },
    [applyColors],
  );

  // Resolve white-label branding for the current host before authentication.
  useEffect(() => {
    let ignore = false;
    service
      .resolvePublic()
      .then((b) => {
        if (!ignore && b) apply(b);
      })
      .catch(() => {});
    return () => {
      ignore = true;
    };
  }, [apply]);

  return (
    <BrandingContext.Provider
      value={{
        logoUrl,
        colors,
        apply,
        applyColors,
        resolveAssetUrl: service.resolveAssetUrl.bind(service),
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBrandingTheme() {
  const ctx = useContext(BrandingContext);
  if (!ctx)
    throw new Error("useBrandingTheme must be used within a BrandingProvider");
  return ctx;
}
